<ng-container *ngIf="qbSubscriptions && !isLoading; else loadingState">
  <div class="registration-page">
    <header class="registration-header">
      <a class="registration-brand" routerLink="/login" aria-label="Quickboard login">
        <img src="assets/images/qb_logo_white.svg" alt="Quickboard" />
      </a>
      <a class="login-link" routerLink="/login">Log in <span aria-hidden="true">→</span></a>
    </header>

    <main class="registration-shell">
      <section class="registration-intro" aria-labelledby="registration-title">
        <div class="step-eyebrow">
          <span class="step-dot" aria-hidden="true"></span>
          Step {{ currentStep }} of {{ registrationSteps.length }} · {{ currentStepDetails.eyebrow }}
        </div>
        <h1 id="registration-title">
          {{ currentStepDetails.titleStart }}<span class="accent">{{ currentStepDetails.titleAccent }}</span>{{
            currentStepDetails.titleEnd
          }}
        </h1>
        <p [ngSwitch]="currentStep">
          <ng-container *ngSwitchCase="1">Select a software license and add any hardware your organization needs.</ng-container>
          <ng-container *ngSwitchCase="2">Set up the account owner and secure your Quickboard login.</ng-container>
          <ng-container *ngSwitchCase="3">Tell us where your organization is located.</ng-container>
          <ng-container *ngSwitchDefault>Confirm delivery, billing, and payment information.</ng-container>
        </p>

        <div
          class="billing-toggle intro-billing-toggle"
          role="group"
          aria-label="Billing cycle"
          *ngIf="currentStep === 1"
        >
          <button
            type="button"
            [class.selected]="!isAnnualBilling"
            [attr.aria-pressed]="!isAnnualBilling"
            (click)="selectBillingPeriod(subscriptionBillingPeriod.Monthly)"
          >
            Monthly
          </button>
          <button
            type="button"
            class="annual-toggle"
            [class.selected]="isAnnualBilling"
            [attr.aria-pressed]="isAnnualBilling"
            (click)="selectBillingPeriod(subscriptionBillingPeriod.Annually)"
          >
            <span>Annual</span>
            <span class="annual-chip">1 month free</span>
          </button>
        </div>

      </section>

      <div class="checkout-layout">
        <section class="checkout-content">
          <mat-stepper
            #registrationStepper
            [linear]="true"
            class="registration-stepper"
            (selectionChange)="onStepSelectionChange($event.selectedIndex)"
          >
            <mat-step label="Software license">
              <section class="stage-section plan-stage" aria-labelledby="plans-heading">
                <div class="section-heading plan-heading">
                  <div>
                    <span class="section-kicker">Software license</span>
                    <h2 id="plans-heading">{{ planSelectionHeading }}</h2>
                  </div>
                </div>

                <div class="mode-toggle" role="group" aria-label="Account type">
                  <button
                    type="button"
                    [class.selected]="
                      qbSubscriptions.selectedSubscriptionType.type ===
                      subscriptionUserTypes.Performance
                    "
                    [attr.aria-pressed]="
                      qbSubscriptions.selectedSubscriptionType.type ===
                      subscriptionUserTypes.Performance
                    "
                    (click)="selectUserType(subscriptionUserTypes.Performance)"
                  >
                    Performance
                  </button>
                  <button
                    type="button"
                    [class.selected]="
                      qbSubscriptions.selectedSubscriptionType.type ===
                      subscriptionUserTypes.Clinic
                    "
                    [attr.aria-pressed]="
                      qbSubscriptions.selectedSubscriptionType.type ===
                      subscriptionUserTypes.Clinic
                    "
                    (click)="selectUserType(subscriptionUserTypes.Clinic)"
                  >
                    Clinic
                  </button>
                </div>

                <p class="availability-note">
                  Available only on iPad (9th generation or later recommended).
                </p>

                <div class="plan-list" role="radiogroup" aria-label="Available plans">
                  <button
                    type="button"
                    class="plan-row"
                    *ngFor="
                      let subscription of qbSubscriptions.selectedSubscriptionType.options
                    "
                    [class.selected]="subscription.selected"
                    [attr.aria-checked]="subscription.selected"
                    role="radio"
                    (click)="selectSubscription(subscription)"
                  >
                    <span class="plan-radio" aria-hidden="true"></span>
                    <span class="plan-copy">
                      <span class="plan-name">{{ subscription.name }}</span>
                      <span class="plan-capacity" *ngIf="subscription.athleteLimit >= 0; else customRoster">
                        {{ subscription.athleteLimit | number }}
                        {{
                          qbSubscriptions.selectedSubscriptionType.type ===
                          subscriptionUserTypes.Clinic
                            ? (subscription.athleteLimit === 1 ? "patient" : "patients")
                            : (subscription.athleteLimit === 1 ? "athlete" : "athletes")
                        }}
                      </span>
                      <ng-template #customRoster>
                        <span class="plan-capacity">Custom roster</span>
                      </ng-template>
                      <span class="plan-note" *ngIf="subscription.price === 0">
                        For the Agility Drill only — not compatible with the Sensor Board.
                      </span>
                      <span
                        class="annual-saving"
                        *ngIf="isAnnualBilling && subscription.discountAnnually > 0"
                      >
                        Save ${{ subscription.discountAnnually | format: 2 }} per year
                      </span>
                    </span>
                    <span class="plan-price">
                      <strong *ngIf="subscription.price === 0; else paidPlan">Free</strong>
                      <ng-template #paidPlan>
                        <strong>${{ effectiveMonthlyPrice(subscription) | format: 2 }}</strong>
                        <small>/ month</small>
                      </ng-template>
                      <small *ngIf="isAnnualBilling && subscription.price > 0">
                        Billed ${{ annualTotal(subscription) | format: 2 }} yearly
                      </small>
                    </span>
                  </button>
                </div>
              </section>

              <section class="stage-section" aria-labelledby="hardware-heading">
                <div class="section-heading">
                  <div>
                    <span class="section-kicker">Optional hardware</span>
                    <h2 id="hardware-heading">Build your setup</h2>
                  </div>
                </div>

                <div class="product-grid">
                  <article class="product-card" *ngFor="let product of productsList; let i = index">
                    <div class="product-image-wrap">
                      <img [src]="productImage(product, i)" [alt]="product.name" />
                    </div>
                    <div class="product-details">
                      <div>
                        <h3>{{ product.name }}</h3>
                        <p *ngIf="product.description">{{ product.description }}</p>
                      </div>
                      <strong>${{ product.price | format: 2 }}</strong>
                    </div>
                    <label class="quantity-control">
                      <span>Quantity</span>
                      <input
                        type="number"
                        min="0"
                        max="10000"
                        [(ngModel)]="product.quantity"
                        #quantity
                        (ngModelChange)="
                          orderDetails.productsChanged(
                            productsList,
                            shippingInformationForm.controls.country.value
                          )
                        "
                        (keypress)="omit_special_char($event)"
                        (input)="
                          quantity.value =
                            quantity.value > 10000
                              ? 10000
                              : quantity.value < 0
                                ? 0
                                : quantity.value
                        "
                        [attr.aria-label]="product.name + ' quantity'"
                      />
                    </label>
                  </article>
                </div>

                <div class="coupon-card">
                  <label for="registration-coupon">Coupon code</label>
                  <div class="coupon-controls">
                    <input
                      id="registration-coupon"
                      type="text"
                      [(ngModel)]="couponCode"
                      autocomplete="off"
                      (keydown.enter)="$event.preventDefault()"
                    />
                    <button
                      type="button"
                      class="secondary-button"
                      *ngIf="!orderDetails.activeCoupon"
                      [disabled]="!couponCode.trim()"
                      (click)="checkCouponValidity()"
                    >
                      Apply coupon
                    </button>
                    <button
                      type="button"
                      class="secondary-button remove"
                      *ngIf="orderDetails.activeCoupon"
                      (click)="removeCoupon()"
                    >
                      Remove {{ orderDetails.activeCoupon.code }}
                    </button>
                  </div>
                  <p class="coupon-message success" *ngIf="couponMessageCode === 0">
                    Coupon applied successfully.
                  </p>
                  <p class="coupon-message error" *ngIf="couponMessageCode === 1">Invalid coupon.</p>
                  <p class="coupon-message warning" *ngIf="couponMessageCode === 2">
                    This coupon is only applicable to annual subscriptions.
                  </p>
                  <p class="coupon-message warning" *ngIf="couponMessageCode === 3">
                    Multiple coupons cannot be applied to the same product.
                  </p>
                </div>
              </section>
            </mat-step>

            <mat-step [stepControl]="personalDetailsForm" label="Account details">
              <section class="stage-section form-stage" aria-labelledby="account-heading">
                <div class="section-heading">
                  <div>
                    <span class="section-kicker">Account owner</span>
                    <h2 id="account-heading">Your login details</h2>
                  </div>
                </div>

                <form
                  class="form-grid"
                  [formGroup]="personalDetailsForm"
                  autocomplete="off"
                  (submit)="$event.preventDefault(); advanceStep()"
                >
                  <mat-form-field appearance="outline" class="form-field full-width">
                    <mat-label>Organization Name</mat-label>
                    <input matInput type="text" formControlName="organizationName" autocomplete="organization" />
                    <mat-hint *ngIf="personalDetailsForm.controls['organizationName'].pending">
                      Checking availability…
                    </mat-hint>
                    <mat-error
                      *ngIf="ValidationHelperService.isInvalid(personalDetailsForm.controls['organizationName'])"
                    >
                      {{ ValidationHelperService.getErrorMessage(personalDetailsForm.controls["organizationName"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field full-width">
                    <mat-label>Username</mat-label>
                    <input matInput type="text" formControlName="username" autocomplete="username" />
                    <mat-hint *ngIf="personalDetailsForm.controls['username'].pending">
                      Checking availability…
                    </mat-hint>
                    <mat-error *ngIf="ValidationHelperService.isInvalid(personalDetailsForm.controls['username'])">
                      {{ ValidationHelperService.getErrorMessage(personalDetailsForm.controls["username"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>First name</mat-label>
                    <input matInput type="text" formControlName="firstName" autocomplete="given-name" />
                    <mat-error *ngIf="ValidationHelperService.isInvalid(personalDetailsForm.controls['firstName'])">
                      {{ ValidationHelperService.getErrorMessage(personalDetailsForm.controls["firstName"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Last name</mat-label>
                    <input matInput type="text" formControlName="lastName" autocomplete="family-name" />
                    <mat-error *ngIf="ValidationHelperService.isInvalid(personalDetailsForm.controls['lastName'])">
                      {{ ValidationHelperService.getErrorMessage(personalDetailsForm.controls["lastName"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field full-width">
                    <mat-label>Email</mat-label>
                    <input matInput type="email" formControlName="email" autocomplete="email" />
                    <mat-error *ngIf="ValidationHelperService.isInvalid(personalDetailsForm.controls['email'])">
                      {{ ValidationHelperService.getErrorMessage(personalDetailsForm.controls["email"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Password</mat-label>
                    <input
                      matInput
                      [type]="passwordVisible ? 'text' : 'password'"
                      formControlName="password"
                      autocomplete="new-password"
                    />
                    <button
                      mat-icon-button
                      matSuffix
                      type="button"
                      class="password-visibility-button"
                      [attr.aria-label]="passwordVisible ? 'Hide password' : 'Show password'"
                      [attr.aria-pressed]="passwordVisible"
                      (click)="togglePasswordVisibility('password')"
                    >
                      <svg *ngIf="!passwordVisible" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
                        <circle cx="12" cy="12" r="2.75" />
                      </svg>
                      <svg *ngIf="passwordVisible" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M3 3l18 18M10.6 6.15A10.8 10.8 0 0 1 12 6c6.5 0 10 6 10 6a17.2 17.2 0 0 1-3.05 3.75M6.15 6.15C3.45 8.05 2 12 2 12s3.5 6 10 6c1.05 0 2-.15 2.85-.4M9.9 9.9a3 3 0 0 0 4.2 4.2" />
                      </svg>
                    </button>
                    <mat-hint>8–32 characters with uppercase, lowercase, number, and symbol.</mat-hint>
                    <mat-error *ngIf="ValidationHelperService.isInvalid(personalDetailsForm.controls['password'])">
                      {{ ValidationHelperService.getErrorMessage(personalDetailsForm.controls["password"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Confirm Password</mat-label>
                    <input
                      matInput
                      [type]="confirmPasswordVisible ? 'text' : 'password'"
                      formControlName="confirmPassword"
                      autocomplete="new-password"
                    />
                    <button
                      mat-icon-button
                      matSuffix
                      type="button"
                      class="password-visibility-button"
                      [attr.aria-label]="confirmPasswordVisible ? 'Hide confirmed password' : 'Show confirmed password'"
                      [attr.aria-pressed]="confirmPasswordVisible"
                      (click)="togglePasswordVisibility('confirmPassword')"
                    >
                      <svg *ngIf="!confirmPasswordVisible" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
                        <circle cx="12" cy="12" r="2.75" />
                      </svg>
                      <svg *ngIf="confirmPasswordVisible" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M3 3l18 18M10.6 6.15A10.8 10.8 0 0 1 12 6c6.5 0 10 6 10 6s-1.1 1.9-3.05 3.75M6.15 6.15C3.45 8.05 2 12 2 12s3.5 6 10 6c1.05 0 2-.15 2.85-.4M9.9 9.9a3 3 0 0 0 4.2 4.2" />
                      </svg>
                    </button>
                    <mat-error
                      *ngIf="ValidationHelperService.isInvalid(personalDetailsForm.controls['confirmPassword'])"
                    >
                      {{ ValidationHelperService.getErrorMessage(personalDetailsForm.controls["confirmPassword"]) }}
                    </mat-error>
                  </mat-form-field>
                </form>
              </section>
            </mat-step>

            <mat-step [stepControl]="addressForm" label="Organization address">
              <section class="stage-section form-stage" aria-labelledby="address-heading">
                <div class="section-heading">
                  <div>
                    <span class="section-kicker">Organization address</span>
                    <h2 id="address-heading">Primary location</h2>
                  </div>
                </div>

                <form
                  class="form-grid"
                  [formGroup]="addressForm"
                  autocomplete="off"
                  (submit)="$event.preventDefault(); advanceStep()"
                >
                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Country</mat-label>
                    <mat-select formControlName="country">
                      <mat-option *ngFor="let country of countriesList" [value]="country">
                        {{ country.name }}
                      </mat-option>
                    </mat-select>
                    <mat-error *ngIf="ValidationHelperService.isInvalid(addressForm.controls['country'])">
                      {{ ValidationHelperService.getErrorMessage(addressForm.controls["country"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>State / Province</mat-label>
                    <mat-select formControlName="state">
                      <mat-option
                        *ngFor="let state of formGeographyData.personalAddress.states"
                        [value]="state.id"
                      >
                        {{ state.name }}
                      </mat-option>
                    </mat-select>
                    <mat-error *ngIf="ValidationHelperService.isInvalid(addressForm.controls['state'])">
                      {{ ValidationHelperService.getErrorMessage(addressForm.controls["state"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>City</mat-label>
                    <input
                      matInput
                      type="text"
                      [formControl]="city"
                      (change)="updateCity($event, city)"
                      [matAutocomplete]="personalCityAuto"
                      autocomplete="address-level2"
                    />
                    <mat-autocomplete #personalCityAuto="matAutocomplete" [displayWith]="displayFn">
                      <mat-option
                        *ngFor="let option of formGeographyData.personalAddress.cities"
                        [value]="option"
                      >
                        {{ option.name }}
                      </mat-option>
                    </mat-autocomplete>
                    <mat-error *ngIf="ValidationHelperService.isInvalid(city)">
                      {{ ValidationHelperService.getErrorMessage(city) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Postal code / Zip code</mat-label>
                    <input matInput type="text" formControlName="zipCode" autocomplete="postal-code" />
                    <mat-error *ngIf="ValidationHelperService.isInvalid(addressForm.controls['zipCode'])">
                      {{ ValidationHelperService.getErrorMessage(addressForm.controls["zipCode"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field full-width">
                    <mat-label>Address</mat-label>
                    <input matInput type="text" formControlName="address" autocomplete="address-line1" />
                    <mat-error *ngIf="ValidationHelperService.isInvalid(addressForm.controls['address'])">
                      {{ ValidationHelperService.getErrorMessage(addressForm.controls["address"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field full-width">
                    <mat-label>Address 2</mat-label>
                    <input matInput type="text" formControlName="address2" autocomplete="address-line2" />
                    <mat-error *ngIf="ValidationHelperService.isInvalid(addressForm.controls['address2'])">
                      {{ ValidationHelperService.getErrorMessage(addressForm.controls["address2"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field full-width">
                    <mat-label>Phone number</mat-label>
                    <input matInput type="tel" formControlName="phone" autocomplete="tel" />
                    <mat-error *ngIf="ValidationHelperService.isInvalid(addressForm.controls['phone'])">
                      {{ ValidationHelperService.getErrorMessage(addressForm.controls["phone"]) }}
                    </mat-error>
                  </mat-form-field>
                </form>
              </section>
            </mat-step>

            <mat-step [stepControl]="shippingInformationForm" label="Checkout">
              <section class="stage-section form-stage" aria-labelledby="shipping-heading">
                <div class="section-heading">
                  <div>
                    <span class="section-kicker">Delivery</span>
                    <h2 id="shipping-heading">Shipping information</h2>
                  </div>
                </div>

                <form class="form-grid" [formGroup]="shippingInformationForm" autocomplete="off">
                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>First Name</mat-label>
                    <input matInput type="text" formControlName="firstName" autocomplete="shipping given-name" />
                    <mat-error
                      *ngIf="ValidationHelperService.isInvalid(shippingInformationForm.controls['firstName'])"
                    >
                      {{ ValidationHelperService.getErrorMessage(shippingInformationForm.controls["firstName"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Last Name</mat-label>
                    <input matInput type="text" formControlName="lastName" autocomplete="shipping family-name" />
                    <mat-error
                      *ngIf="ValidationHelperService.isInvalid(shippingInformationForm.controls['lastName'])"
                    >
                      {{ ValidationHelperService.getErrorMessage(shippingInformationForm.controls["lastName"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field full-width">
                    <mat-label>Shipping Address</mat-label>
                    <input matInput type="text" formControlName="address" autocomplete="shipping address-line1" />
                    <mat-error
                      *ngIf="ValidationHelperService.isInvalid(shippingInformationForm.controls['address'])"
                    >
                      {{ ValidationHelperService.getErrorMessage(shippingInformationForm.controls["address"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field full-width">
                    <mat-label>Shipping Address 2</mat-label>
                    <input matInput type="text" formControlName="address2" autocomplete="shipping address-line2" />
                    <mat-error
                      *ngIf="ValidationHelperService.isInvalid(shippingInformationForm.controls['address2'])"
                    >
                      {{ ValidationHelperService.getErrorMessage(shippingInformationForm.controls["address2"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Shipping Country</mat-label>
                    <mat-select formControlName="country">
                      <mat-option *ngFor="let country of countriesList" [value]="country">
                        {{ country.name }}
                      </mat-option>
                    </mat-select>
                    <mat-error
                      *ngIf="ValidationHelperService.isInvalid(shippingInformationForm.controls['country'])"
                    >
                      {{ ValidationHelperService.getErrorMessage(shippingInformationForm.controls["country"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Shipping State</mat-label>
                    <mat-select formControlName="state">
                      <mat-option
                        *ngFor="let state of formGeographyData.shippingAddress.states"
                        [value]="state.id"
                      >
                        {{ state.name }}
                      </mat-option>
                    </mat-select>
                    <mat-error
                      *ngIf="ValidationHelperService.isInvalid(shippingInformationForm.controls['state'])"
                    >
                      {{ ValidationHelperService.getErrorMessage(shippingInformationForm.controls["state"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Shipping City</mat-label>
                    <input
                      matInput
                      type="text"
                      [formControl]="shippingCity"
                      (change)="updateCity($event, shippingCity)"
                      [matAutocomplete]="shippingCityAuto"
                      autocomplete="shipping address-level2"
                    />
                    <mat-autocomplete #shippingCityAuto="matAutocomplete" [displayWith]="displayFn">
                      <mat-option
                        *ngFor="let option of formGeographyData.shippingAddress.cities"
                        [value]="option"
                      >
                        {{ option.name }}
                      </mat-option>
                    </mat-autocomplete>
                    <mat-error *ngIf="ValidationHelperService.isInvalid(shippingCity)">
                      {{ ValidationHelperService.getErrorMessage(shippingCity) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Shipping Zip Code</mat-label>
                    <input matInput type="text" formControlName="zipCode" autocomplete="shipping postal-code" />
                    <mat-error
                      *ngIf="ValidationHelperService.isInvalid(shippingInformationForm.controls['zipCode'])"
                    >
                      {{ ValidationHelperService.getErrorMessage(shippingInformationForm.controls["zipCode"]) }}
                    </mat-error>
                  </mat-form-field>
                </form>
              </section>

              <section class="stage-section form-stage" *ngIf="paymentRequired" aria-labelledby="payment-heading">
                <div class="section-heading">
                  <div>
                    <span class="section-kicker">Payment</span>
                    <h2 id="payment-heading">Card information</h2>
                  </div>
                </div>

                <form class="form-grid" [formGroup]="billingInformationForm" autocomplete="off">
                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>First Name on Account</mat-label>
                    <input matInput type="text" formControlName="firstName" autocomplete="cc-given-name" />
                    <mat-error
                      *ngIf="ValidationHelperService.isInvalid(billingInformationForm.controls['firstName'])"
                    >
                      {{ ValidationHelperService.getErrorMessage(billingInformationForm.controls["firstName"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Last Name on Account</mat-label>
                    <input matInput type="text" formControlName="lastName" autocomplete="cc-family-name" />
                    <mat-error
                      *ngIf="ValidationHelperService.isInvalid(billingInformationForm.controls['lastName'])"
                    >
                      {{ ValidationHelperService.getErrorMessage(billingInformationForm.controls["lastName"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field full-width">
                    <mat-label>Card Number</mat-label>
                    <input
                      matInput
                      type="text"
                      inputmode="numeric"
                      formControlName="cardNumber"
                      autocomplete="cc-number"
                      (keypress)="omit_special_char($event)"
                    />
                    <mat-error
                      *ngIf="ValidationHelperService.isInvalid(billingInformationForm.controls['cardNumber'])"
                    >
                      {{ ValidationHelperService.getErrorMessage(billingInformationForm.controls["cardNumber"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Expiration Month</mat-label>
                    <mat-select [(ngModel)]="selectedMonth" [ngModelOptions]="{ standalone: true }">
                      <mat-option *ngFor="let month of monthVector" [value]="month">{{ month }}</mat-option>
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Expiration Year</mat-label>
                    <mat-select
                      [(ngModel)]="selectedYear"
                      [ngModelOptions]="{ standalone: true }"
                      (selectionChange)="validateDate()"
                    >
                      <mat-option *ngFor="let year of yearVector" [value]="year">{{ year }}</mat-option>
                    </mat-select>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>CVV</mat-label>
                    <input
                      matInput
                      type="text"
                      inputmode="numeric"
                      formControlName="CVV"
                      autocomplete="cc-csc"
                    />
                    <mat-error *ngIf="ValidationHelperService.isInvalid(billingInformationForm.controls['CVV'])">
                      {{ ValidationHelperService.getErrorMessage(billingInformationForm.controls["CVV"]) }}
                    </mat-error>
                  </mat-form-field>
                </form>
              </section>

              <section class="stage-section form-stage" aria-labelledby="billing-address-heading">
                <div class="section-heading">
                  <div>
                    <span class="section-kicker">Billing</span>
                    <h2 id="billing-address-heading">Billing address</h2>
                  </div>
                </div>

                <form class="form-grid" [formGroup]="billingAddressForm" autocomplete="off">
                  <mat-form-field appearance="outline" class="form-field full-width">
                    <mat-label>Billing Address</mat-label>
                    <input matInput type="text" formControlName="address" autocomplete="billing address-line1" />
                    <mat-error *ngIf="ValidationHelperService.isInvalid(billingAddressForm.controls['address'])">
                      {{ ValidationHelperService.getErrorMessage(billingAddressForm.controls["address"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field full-width">
                    <mat-label>Billing Address 2</mat-label>
                    <input matInput type="text" formControlName="address2" autocomplete="billing address-line2" />
                    <mat-error *ngIf="ValidationHelperService.isInvalid(billingAddressForm.controls['address2'])">
                      {{ ValidationHelperService.getErrorMessage(billingAddressForm.controls["address2"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Billing Country</mat-label>
                    <mat-select formControlName="country">
                      <mat-option *ngFor="let country of countriesList" [value]="country">
                        {{ country.name }}
                      </mat-option>
                    </mat-select>
                    <mat-error *ngIf="ValidationHelperService.isInvalid(billingAddressForm.controls['country'])">
                      {{ ValidationHelperService.getErrorMessage(billingAddressForm.controls["country"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Billing State</mat-label>
                    <mat-select formControlName="state">
                      <mat-option
                        *ngFor="let state of formGeographyData.billingAddress.states"
                        [value]="state.id"
                      >
                        {{ state.name }}
                      </mat-option>
                    </mat-select>
                    <mat-error *ngIf="ValidationHelperService.isInvalid(billingAddressForm.controls['state'])">
                      {{ ValidationHelperService.getErrorMessage(billingAddressForm.controls["state"]) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Billing City</mat-label>
                    <input
                      matInput
                      type="text"
                      [formControl]="billingCity"
                      (change)="updateCity($event, billingCity)"
                      [matAutocomplete]="billingCityAuto"
                      autocomplete="billing address-level2"
                    />
                    <mat-autocomplete #billingCityAuto="matAutocomplete" [displayWith]="displayFn">
                      <mat-option
                        *ngFor="let option of formGeographyData.billingAddress.cities"
                        [value]="option"
                      >
                        {{ option.name }}
                      </mat-option>
                    </mat-autocomplete>
                    <mat-error *ngIf="ValidationHelperService.isInvalid(billingCity)">
                      {{ ValidationHelperService.getErrorMessage(billingCity) }}
                    </mat-error>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="form-field">
                    <mat-label>Billing Zip Code</mat-label>
                    <input matInput type="text" formControlName="zipCode" autocomplete="billing postal-code" />
                    <mat-error *ngIf="ValidationHelperService.isInvalid(billingAddressForm.controls['zipCode'])">
                      {{ ValidationHelperService.getErrorMessage(billingAddressForm.controls["zipCode"]) }}
                    </mat-error>
                  </mat-form-field>
                </form>
              </section>

              <section class="terms-card">
                <div class="terms-consent-row">
                  <mat-checkbox
                    class="terms-checkbox"
                    [checked]="termsAgreed"
                    (change)="termsAgreed = $event.checked"
                    aria-label="Accept the Terms and Conditions"
                  ></mat-checkbox>
                  <div class="terms-consent-copy">
                    <span>I have read and accepted the</span>
                    <a
                      href=""
                      (click)="$event.preventDefault(); openTermsModal()"
                    >Terms and Conditions</a>
                    <a
                      class="download-link"
                      href="assets/pdf/Quick%20Board%20Terms.pdf"
                      target="_blank"
                      aria-label="Download terms and conditions"
                      title="Download terms and conditions"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 3v11m0 0 4-4m-4 4-4-4M5 19h14" />
                      </svg>
                    </a>
                  </div>
                </div>
                <p *ngIf="!termsAgreed && shippingInformationForm.touched" class="terms-hint">
                  Acceptance is required to complete registration.
                </p>
              </section>
            </mat-step>
          </mat-stepper>
        </section>

        <aside class="summary-column" aria-label="Order summary">
          <div class="order-summary">
            <ng-container *ngTemplateOutlet="summaryDetails"></ng-container>
            <div class="summary-actions">
              <button
                type="button"
                class="back-button"
                *ngIf="currentStep > 1"
                (click)="backStep()"
              >
                Back
              </button>
              <button
                type="button"
                class="primary-button"
                [disabled]="primaryActionDisabled"
                (click)="advanceStep()"
              >
                {{ primaryActionLabel }}
              </button>
            </div>
          </div>
          <button
            type="button"
            class="restart-registration-button"
            (click)="openRestartConfirmation()"
          >
            Restart registration
          </button>
        </aside>
      </div>

      <footer class="registration-footer">
        <div class="trust-signals" aria-label="Quickboard credentials">
          <span>Published in IJSPT &amp; JSCR</span>
          <span>Trusted by NBA, NFL, MLB teams</span>
          <span>Made in the USA</span>
        </div>
        <div class="footer-links">
          <a routerLink="/privacy-policy" target="_blank">Privacy Policy</a>
          <a
            class="footer-download"
            href="assets/pdf/Quick%20Board%20Privacy%20Policy.pdf"
            target="_blank"
            aria-label="Download privacy policy"
            title="Download privacy policy"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3v11m0 0 4-4m-4 4-4-4M5 19h14" />
            </svg>
          </a>
        </div>
      </footer>
    </main>

    <div class="mobile-summary" [class.expanded]="summaryExpanded">
      <div class="mobile-summary-panel" *ngIf="summaryExpanded">
        <ng-container *ngTemplateOutlet="summaryDetails"></ng-container>
        <div class="mobile-restart-wrap">
          <button
            type="button"
            class="restart-registration-button mobile-restart-button"
            (click)="openRestartConfirmation()"
          >
            Restart registration
          </button>
        </div>
      </div>
      <div class="mobile-summary-bar">
        <button
          type="button"
          class="mobile-back"
          *ngIf="currentStep > 1"
          (click)="backStep()"
          aria-label="Go back"
        >
          ←
        </button>
        <button
          type="button"
          class="mobile-summary-toggle"
          (click)="toggleSummary()"
          [attr.aria-expanded]="summaryExpanded"
        >
          <span>
            <strong>{{ selectedSubscription?.name || "Plan summary" }}</strong>
            <small>${{ orderDetails.totalPrice | format: 2 }} due today</small>
          </span>
          <span aria-hidden="true">{{ summaryExpanded ? "↓" : "↑" }}</span>
        </button>
        <button
          type="button"
          class="primary-button mobile-primary"
          [disabled]="primaryActionDisabled"
          (click)="advanceStep()"
        >
          {{ currentStep < 4 ? "Continue" : (paymentRequired ? "Place order" : "Create account") }}
        </button>
      </div>
    </div>

    <div
      class="restart-dialog-backdrop"
      *ngIf="restartConfirmationOpen"
      (click)="closeRestartConfirmation()"
    >
      <section
        class="restart-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="restart-dialog-title"
        aria-describedby="restart-dialog-description"
        (click)="$event.stopPropagation()"
      >
        <span class="section-kicker">Start over</span>
        <h2 id="restart-dialog-title">Restart registration?</h2>
        <p id="restart-dialog-description">
          All information entered in this registration form will be permanently deleted.
          This action cannot be undone.
        </p>
        <div class="restart-dialog-actions">
          <button type="button" class="back-button" (click)="closeRestartConfirmation()">
            Cancel
          </button>
          <button type="button" class="primary-button" (click)="confirmRestartRegistration()">
            Restart registration
          </button>
        </div>
      </section>
    </div>
  </div>
</ng-container>

<ng-template #summaryDetails>
  <div class="summary-heading">
    <span>Order summary</span>
    <strong>{{ selectedSubscription?.name || "Select a plan" }}</strong>
    <small *ngIf="selectedSubscription">
      {{ isAnnualBilling ? "Annual" : "Monthly" }} billing ·
      <ng-container *ngIf="selectedSubscription.athleteLimit >= 0">
        {{ selectedSubscription.athleteLimit | number }}
        {{
          qbSubscriptions.selectedSubscriptionType.type === subscriptionUserTypes.Clinic
            ? (selectedSubscription.athleteLimit === 1 ? "patient" : "patients")
            : (selectedSubscription.athleteLimit === 1 ? "athlete" : "athletes")
        }}
      </ng-container>
      <ng-container *ngIf="selectedSubscription.athleteLimit < 0">Custom roster</ng-container>
    </small>
  </div>

  <div class="summary-lines" aria-live="polite">
    <div class="summary-line" *ngIf="selectedSubscription">
      <span>{{ selectedSubscription.name }} plan</span>
      <strong
        >${{
          orderDetails.activeCoupon?.discountType === discountType.Days
            ? (0 | format: 2)
            : (orderDetails.subscriptionPrice | format: 2)
        }}</strong
      >
    </div>

    <ng-container *ngFor="let product of productsList">
      <div class="summary-line" *ngIf="product.quantity > 0">
        <span>{{ product.quantity }} × {{ product.name }}</span>
        <strong>${{ product.quantity * product.price | format: 2 }}</strong>
      </div>
    </ng-container>

    <div class="summary-line" *ngIf="orderDetails.shippingCost">
      <span>
        Shipping
        <span
          class="shipping-tooltip"
          matTooltip="Rate excludes taxes, duties, and other charges that may apply."
          aria-label="Shipping cost information"
          >i</span
        >
      </span>
      <strong>${{ orderDetails.shippingCost | format: 2 }}</strong>
    </div>

    <div class="summary-line discount" *ngIf="orderDetails.activeCoupon">
      <span>Coupon {{ orderDetails.activeCoupon.code }}</span>
      <strong *ngIf="orderDetails.activeCoupon.discountType === discountType.FixedAmount">
        −${{ orderDetails.activeCoupon.discount | format: 2 }}
      </strong>
      <strong *ngIf="orderDetails.activeCoupon.discountType === discountType.Percentage">
        −{{ orderDetails.activeCoupon.discount }}%
      </strong>
      <strong *ngIf="orderDetails.activeCoupon.discountType === discountType.Days">
        {{ orderDetails.activeCoupon.discount }} free days
      </strong>
    </div>

    <div
      class="summary-line discount"
      *ngIf="
        orderDetails.discounts &&
        (!orderDetails.activeCoupon ||
          orderDetails.activeCoupon.discountType !== discountType.Days)
      "
    >
      <span>Annual discount</span>
      <strong>−${{ orderDetails.discounts | format: 2 }}</strong>
    </div>
  </div>

  <div class="summary-total">
    <span>Due today</span>
    <strong>${{ orderDetails.totalPrice | format: 2 }}</strong>
  </div>

  <p
    class="trial-message"
    *ngIf="
      orderDetails.activeCoupon?.discountType === discountType.Days &&
      orderDetails.activeCoupon.expiresOn
    "
  >
    Free trial expires {{ orderDetails.activeCoupon.expiresOn | date }}. The subscription will then
    renew at ${{ orderDetails.subscriptionPrice - orderDetails.discounts | format: 2 }}.
  </p>
</ng-template>

<ng-template #loadingState>
  <div class="registration-loading" role="status" aria-live="polite">
    <img src="assets/images/qb_logo_white.svg" alt="Quickboard" />
    <mat-spinner diameter="46"></mat-spinner>
    <p>Preparing registration…</p>
  </div>
</ng-template>
